import { computePosition, flip, shift } from '@floating-ui/dom'
import { posToDOMRect, VueRenderer } from '@tiptap/vue-3'
import type { Editor } from '@tiptap/core'
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion'
import type { Component } from 'vue'

import { SfLog } from '@app/adapters/logger'
import type { MentionListRef, SuggestionRenderer } from './types'

async function updatePosition(
  editor: Editor,
  element: HTMLElement
): Promise<void> {
  const virtualElement = {
    getBoundingClientRect: (): DOMRect =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      ),
  }

  let x, y, strategy

  try {
    ;({ x, y, strategy } = await computePosition(virtualElement, element, {
      placement: 'bottom-start',
      strategy: 'absolute',
      middleware: [flip(), shift({ padding: 8 })],
    }))
  } catch (error) {
    SfLog.error('Failed to position suggestion dropdown:', { error })
  }

  Object.assign(element.style, {
    width: 'max-content',
    position: strategy,
    left: `${x}px`,
    top: `${y}px`,
  })
}

interface SuggestionRendererConfig<TProps = Record<string, unknown>> {
  /** The Vue component to render in the suggestion dropdown */
  component: Component

  /**
   * Function that computes the props to pass to the component
   * Called on both onStart and onUpdate
   * @param suggestionProps - The props provided by TipTap's suggestion plugin
   * @returns The props to pass to the rendered component
   */
  getProps: (suggestionProps: SuggestionProps) => TProps

  /**
   * Whether to include a forceUpdate method on the returned renderer
   * Useful when external state changes need to manually trigger component updates
   */
  includeForceUpdate?: boolean
}

/**
 * Factory function that creates a TipTap suggestion renderer with common behavior
 *
 * This eliminates duplication across multiple suggestion renderer implementations
 * by providing shared functionality for positioning, lifecycle management, and
 * keyboard handling.
 *
 * @example
 * ```ts
 * const renderer = createSuggestionRenderer({
 *   component: MyDropdownComponent,
 *   getProps: (props) => ({ items: props.items, command: props.command }),
 *   includeForceUpdate: true
 * })
 * ```
 */
export function createSuggestionRenderer<TProps = Record<string, unknown>>(
  config: SuggestionRendererConfig<TProps>
): SuggestionRenderer & { forceUpdate?: () => void } {
  const { component, getProps, includeForceUpdate = false } = config
  let vueComponent: VueRenderer | null = null
  let scrollHandler: (() => void) | null = null
  let resizeObserver: ResizeObserver | null = null

  const forceUpdate = includeForceUpdate
    ? (): void => {
        if (vueComponent) {
          // Recompute props and update component
          // Note: We can't access SuggestionProps here, so components using
          // forceUpdate must store their own state or use getter functions
          const newProps = getProps({} as SuggestionProps)
          vueComponent.updateProps(newProps as Record<string, unknown>)
        }
      }
    : undefined

  const renderer: SuggestionRenderer = {
    onStart(props: SuggestionProps): void {
      const componentProps = getProps(props)

      vueComponent = new VueRenderer(component, {
        props: {
          ...props,
          ...componentProps,
        },
        editor: props.editor,
      })

      if (!props.clientRect || !vueComponent.element) return

      const el = vueComponent.element as HTMLElement
      el.style.position = 'absolute'
      document.body.appendChild(el)

      void updatePosition(props.editor, el)

      resizeObserver = new ResizeObserver(() => {
        void updatePosition(props.editor, el)
      })
      resizeObserver.observe(el)

      scrollHandler = (): void => {
        void updatePosition(props.editor, el)
      }
      window.addEventListener('scroll', scrollHandler, true)
    },

    onUpdate(props: SuggestionProps): void {
      const componentProps = getProps(props)

      vueComponent?.updateProps({
        ...props,
        ...componentProps,
      })

      if (!props.clientRect || !vueComponent?.element) return

      void updatePosition(props.editor, vueComponent.element as HTMLElement)
    },

    onKeyDown(props: SuggestionKeyDownProps): boolean {
      if (props.event.key === 'Escape') {
        vueComponent?.destroy()
        return true
      }

      return (vueComponent?.ref as MentionListRef)?.onKeyDown?.(props) ?? false
    },

    onExit(_props: SuggestionProps): void {
      vueComponent?.element?.remove()
      vueComponent?.destroy()
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, true)
        scrollHandler = null
      }
    },
  }

  return forceUpdate ? { ...renderer, forceUpdate } : renderer
}
